import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyHppMode({ brand, today, Copy }: ModeRendererProps) {
  // HPP gradient: protein spectrum
  const hppGradient = (
    <defs>
      <linearGradient id="hppGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#9C27B0" />
        <stop offset="50%" stopColor="#E91E63" />
        <stop offset="100%" stopColor="#FF5722" />
      </linearGradient>
      <radialGradient id="foldGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#E91E63" stopOpacity={0.6} />
        <stop offset="100%" stopColor="#9C27B0" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Energy landscapes: protein folding terrain
  const energyLandscapes: JSX.Element[] = [];
  for (let i = 0; i < 14; i++) {
    const y = 120 + i * 65;
    const points = [];
    for (let j = 0; j < 13; j++) {
      const x = 70 + j * 55;
      const energy = Math.sin(i * 0.5 + j * 0.4) * 40 + Math.cos(i * j * 0.1) * 20;
      points.push(`${x},${y + energy}`);
    }
    energyLandscapes.push(
      <polyline
        key={`energy-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#hppGrad)"
        strokeWidth={1.5}
        opacity={0.4}
      />
    );
  }

  // Contact maps: protein interaction matrix
  const contactMaps: JSX.Element[] = [];
  for (let row = 0; row < 14; row++) {
    for (let col = row; col < 14; col++) {
      const x = 100 + col * 45;
      const y = 120 + row * 65;
      const contact = Math.random() > 0.7;
      if (contact) {
        const strength = 0.3 + Math.random() * 0.5;
        contactMaps.push(
          <circle
            key={`contact-${row}-${col}`}
            cx={x}
            cy={y}
            r={6 + strength * 8}
            fill="url(#hppGrad)"
            opacity={strength}
          />
        );
      }
    }
  }

  // Motif grids: structural domains
  const motifGrids: JSX.Element[] = [];
  const motifs = ["α", "β", "Ω", "⊥"];
  for (let i = 0; i < 10; i++) {
    const angle = (i * Math.PI * 2) / 10;
    const radius = 220;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    const motif = motifs[i % 4];
    motifGrids.push(
      <g key={`motif-${i}`}>
        <rect
          x={x - 20}
          y={y - 20}
          width={40}
          height={40}
          fill="url(#foldGlow)"
          stroke="url(#hppGrad)"
          strokeWidth={2}
          opacity={0.5}
          rx={6}
        />
        <text
          x={x}
          y={y + 7}
          fontFamily="monospace"
          fontSize={20}
          fontWeight="bold"
          fill="#FFFFFF"
          textAnchor="middle"
        >
          {motif}
        </text>
      </g>
    );
  }

  // Folding trajectories: conformational paths
  const foldingTrajectories: JSX.Element[] = [];
  for (let i = 0; i < 6; i++) {
    const points = [];
    for (let j = 0; j < 24; j++) {
      const angle = (j * Math.PI * 2) / 24 + i;
      const radius = 120 + j * 5;
      const x = 397 + radius * Math.cos(angle);
      const y = 561.5 + radius * Math.sin(angle);
      points.push(`${x},${y}`);
    }
    foldingTrajectories.push(
      <polyline
        key={`fold-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#hppGrad)"
        strokeWidth={2}
        opacity={0.3}
      />
    );
  }

  // Central proteome core
  const proteomeCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={100} fill="url(#foldGlow)" />
      <circle cx={397} cy={561.5} r={70} fill="none" stroke="#FF5722" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={50} fill="none" stroke="#E91E63" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={30} fill="none" stroke="#9C27B0" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={20}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        HPP
      </text>
    </g>
  );

  return (
    <g>
      {hppGradient}
      {energyLandscapes}
      {contactMaps}
      {foldingTrajectories}
      {motifGrids}
      {proteomeCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
